/**
 * Static second row of the SRP header: horizontally scrollable facet pills.
 * Renders from initial facets data (from Algolia) so the full desktop filter bar
 * is present in the static shell without lazy loading.
 */
import React, { useMemo } from 'react';
import { Icon, type IconName } from '../../../components/icons/Icon';
import { Link } from '../../../components/Link';
import { CompanyName } from '../../../components/CompanyName';
import { Logo } from '../../../components/navigation';
import { generateUrlFromIndexUiState } from '../../../utils/algolia/url-handling';
import {
  shouldIncludeFacetInTopFilters,
  TOP_FILTER_ATTRIBUTE_ORDER,
  ALL_TOP_FILTER_ATTRIBUTES_ORDER,
  getTopFilterColorClass,
  TOP_FILTER_EXCLUDED_COLOR,
} from '../../../layouts/helpers/topFilterConfig';
import type { SrpFacetsData } from '../../../features/srp/SrpViewModel';

const MAX_FACETS = 20;

/** Icon name per attribute to match TopFilters (file-based icons). */
const ATTRIBUTE_ICON_NAME: Record<string, IconName> = {
  remoteness: 'mapPin',
  location: 'mapPin',
  jobType: 'briefcase',
  normalisedRoleLevel: 'chartBar',
  companyIndustry: 'buildingOffice2',
  companyId: 'buildingOffice2',
  requiredLanguages: 'globeAlt',
  benefits: 'briefcase',
  skillsAndTechnologies: 'briefcase',
  field: 'chartBar',
  educationLevel: 'chartBar',
  peopleManagementTier: 'chartBar',
};

/** Inactive pill style (matches TopFilters). */
const PILL_CLASS_INACTIVE =
  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600 border-2';

type StaticRenderItem =
  | { kind: 'bracket'; char: '[' | ']'; key: string }
  | { kind: 'connector'; text: 'AND' | 'OR'; key: string }
  | { kind: 'pill'; pill: { attribute: string; value: string; label: string; count: number; isActive: boolean; isExcluded: boolean }; key: string };

export interface StaticTopFiltersProps {
  facets: SrpFacetsData;
  refinementList: Record<string, string[]>;
  pathname: string;
  indexName: string;
  query: string;
  /** When set, show logo on left of scroll row on mobile (lg:hidden), matching TopFilters. */
  locale?: string;
  logoTooltip?: string;
}

function toggleRefinement(
  refinementList: Record<string, string[]>,
  attribute: string,
  value: string
): Record<string, string[]> {
  const current = refinementList[attribute] ?? [];
  const has = current.some((v) => v.toLowerCase() === value.toLowerCase());
  const next = has
    ? current.filter((v) => v.toLowerCase() !== value.toLowerCase())
    : [...current, value];
  const out = { ...refinementList };
  if (next.length === 0) {
    delete out[attribute];
  } else {
    out[attribute] = next;
  }
  return out;
}

export function StaticTopFilters({
  facets,
  refinementList,
  pathname,
  indexName,
  query,
  locale,
  logoTooltip,
}: StaticTopFiltersProps): React.ReactElement | null {
  const renderItems = useMemo((): StaticRenderItem[] => {
    const isInRefinement = (attr: string, value: string): boolean =>
      (refinementList[attr] ?? []).some((v) => v.toLowerCase() === value.toLowerCase());
    const isInExclude = (attr: string, value: string): boolean =>
      (refinementList[`exclude_${attr}`] ?? []).some((v) => v.toLowerCase() === value.toLowerCase());

    type Pill = { attribute: string; value: string; label: string; count: number; isActive: boolean; isExcluded: boolean };
    const raw: Pill[] = [];
    for (const attr of ALL_TOP_FILTER_ATTRIBUTES_ORDER) {
      const items = facets[attr] ?? [];
      for (const item of items) {
        raw.push({
          attribute: attr,
          value: item.value,
          label: item.label,
          count: item.count,
          isActive: item.selected ?? isInRefinement(attr, item.value),
          isExcluded: isInExclude(attr, item.value),
        });
      }
    }
    const allowedFacets = raw.filter((f) => shouldIncludeFacetInTopFilters(f.attribute));
    const activeFacets = allowedFacets.filter((f) => f.isActive || f.isExcluded);
    const inactiveFacets = allowedFacets.filter((f) => !f.isActive && !f.isExcluded);

    const attributeOrder = TOP_FILTER_ATTRIBUTE_ORDER as readonly string[];
    const activeByAttr = new Map<string, Pill[]>();
    for (const f of activeFacets) {
      if (!activeByAttr.has(f.attribute)) activeByAttr.set(f.attribute, []);
      activeByAttr.get(f.attribute)!.push(f);
    }

    const sortedActiveGroups: Pill[][] = [];
    for (const attr of attributeOrder) {
      const list = activeByAttr.get(attr);
      if (list) sortedActiveGroups.push(list);
    }
    const addedAttributes = new Set(attributeOrder.filter((a) => activeByAttr.has(a)));
    for (const f of activeFacets) {
      if (!addedAttributes.has(f.attribute)) {
        sortedActiveGroups.push([f]);
        addedAttributes.add(f.attribute);
      }
    }

    const sortedInactive = [...inactiveFacets].sort((a, b) => b.count - a.count);
    const remainingSlots = Math.max(0, MAX_FACETS - activeFacets.length);
    const limitedInactive = sortedInactive.slice(0, remainingSlots);

    const items: StaticRenderItem[] = [];
    sortedActiveGroups.forEach((group, groupIdx) => {
      const nextGroup = sortedActiveGroups[groupIdx + 1];
      const isGrouped = group.length > 1;
      const groupHasActive = group[0].isActive || group[0].isExcluded;
      const nextGroupHasActive = nextGroup && (nextGroup[0].isActive || nextGroup[0].isExcluded);
      const shouldShowConnectorBetweenGroups = !!(groupHasActive && nextGroupHasActive);

      if (isGrouped && groupHasActive) {
        items.push({ kind: 'bracket', char: '[', key: `group-${groupIdx}-open` });
      }

      group.forEach((pill, facetIdx) => {
        const nextPill = group[facetIdx + 1];
        const shouldShowOr =
          isGrouped &&
          !!nextPill &&
          (pill.isActive || pill.isExcluded) &&
          (nextPill.isActive || nextPill.isExcluded);

        items.push({ kind: 'pill', pill, key: `pill-${pill.attribute}-${pill.value}-${groupIdx}-${facetIdx}` });
        if (shouldShowOr) {
          items.push({ kind: 'connector', text: 'OR', key: `or-${groupIdx}-${facetIdx}` });
        }
      });

      if (isGrouped && groupHasActive) {
        items.push({ kind: 'bracket', char: ']', key: `group-${groupIdx}-close` });
      }
      if (shouldShowConnectorBetweenGroups) {
        items.push({ kind: 'connector', text: 'AND', key: `and-${groupIdx}` });
      }
    });

    limitedInactive.forEach((pill, i) => {
      items.push({ kind: 'pill', pill, key: `inactive-${pill.attribute}-${pill.value}-${i}` });
    });

    return items;
  }, [facets, refinementList]);

  if (renderItems.length === 0) return null;

  return (
    <aside className="relative" style={{ height: '64px' }} aria-label="Filter categories">
      <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-white dark:from-gray-800 to-transparent pointer-events-none z-10 lg:left-0" />
      <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-white dark:from-gray-800 to-transparent pointer-events-none z-10" />
      <div className="h-full flex items-center min-w-0">
        {locale != null && logoTooltip != null && (
          <div className="flex-shrink-0 pl-3 pr-2 lg:hidden flex items-center">
            <Logo tooltipLabel={logoTooltip} locale={locale} variant="icon" />
          </div>
        )}
        <div
          className="h-full flex-1 min-w-0 flex items-center overflow-x-auto pl-2 pr-4 lg:pl-2 lg:pr-4 xl:px-20 gap-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent' }}
        >
          {renderItems.map((item) => {
            if (item.kind === 'bracket') {
              return (
                <span
                  key={item.key}
                  className="text-gray-400 dark:text-gray-500 text-lg font-light select-none flex-shrink-0"
                >
                  {item.char}
                </span>
              );
            }
            if (item.kind === 'connector') {
              return (
                <span
                  key={item.key}
                  className="px-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400 select-none flex-shrink-0"
                >
                  {item.text}
                </span>
              );
            }
            const { pill } = item;
            const href = generateUrlFromIndexUiState(
              {
                query,
                refinementList: toggleRefinement(refinementList, pill.attribute, pill.value),
                page: 0,
              },
              indexName,
              pathname
            );
            const activeClass = pill.isExcluded
              ? `${TOP_FILTER_EXCLUDED_COLOR} shadow-sm border-2`
              : pill.isActive
                ? `${getTopFilterColorClass(pill.attribute, false)} shadow-sm border-2`
                : PILL_CLASS_INACTIVE;

            return (
              <Link
                key={item.key}
                href={href ?? '#'}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-medium text-sm whitespace-nowrap border-2 flex-shrink-0 ${activeClass}`}
                aria-label={`${pill.label} (${pill.count} jobs)`}
              >
                {ATTRIBUTE_ICON_NAME[pill.attribute] ? (
                  <Icon
                    name={ATTRIBUTE_ICON_NAME[pill.attribute]}
                    useMaskForColor
                    className="w-3 h-3 flex-shrink-0"
                  />
                ) : null}
                {pill.isExcluded && (
                  <span className="text-red-500 dark:text-red-400 font-bold">✗</span>
                )}
                {pill.attribute === 'companyId' ? (
                  <CompanyName name={pill.label} />
                ) : (
                  <span>{pill.label}</span>
                )}
                <span className="text-xs opacity-75">({pill.count})</span>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
